'use client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { NavigationBlockingContext } from '../context/NavigationBlockingContextProvider';
import type { Url } from 'next/dist/shared/lib/router/router';

export type UseBlockingRouterOptions = Parameters<
	ReturnType<typeof useRouter>['push']
>[1] & {
	// an additional overrideBlockers to let us bypass the blockers if we wanted
	overrideBlockers?: string[];
	/* or if we want to keep the blocker in state but ignore it once, like when toggling
	between edit and view, we can just bypass it. */
	bypassBlockers?: string[];
};

type BlockingRouterEventName =
	| 'beforeHistoryChange'
	| 'routeChangeStart'
	| 'routeChangeComplete'
	| 'routeChangeError';
type BlockingRouterEventHandler = ( url: string ) => void;

type BlockingRouterEvents = {
	on: (
		eventName: BlockingRouterEventName,
		handler: BlockingRouterEventHandler
	) => void;
	off: (
		eventName: BlockingRouterEventName,
		handler: BlockingRouterEventHandler
	) => void;
};

type UseBlockingRouterRef = {
	targetUrl: string;
	resolve: ( value: boolean ) => void;
};

type UseBlockingRouterReturn = Omit<ReturnType<typeof useRouter>, 'push'> & {
	asPath: string;
	events: BlockingRouterEvents;
	query: Record<string, string | string[]>;
	pathname: string;
	push: ( href: Url, options?: UseBlockingRouterOptions ) => Promise<boolean>;
};

export const useBlockingRouter = (): UseBlockingRouterReturn => {
	const router = useRouter();
	const rawPathname = usePathname();
	const pathname = rawPathname ?? '';
	const searchParams = useSearchParams();
	const ctx = useContext( NavigationBlockingContext );

	const currentUrl = useMemo( () => {
		const queryString = searchParams?.toString();
		return queryString ? `${ pathname }?${ queryString }` : pathname;
	}, [ pathname, searchParams ] );
	const asPath = currentUrl;

	const currentUrlRef = useRef( currentUrl );
	const pendingPushRef = useRef<UseBlockingRouterRef | null>( null );
	const eventHandlersRef = useRef<
		Map<BlockingRouterEventName, Set<BlockingRouterEventHandler>>
	>( new Map() );

	const getNavigationTarget = useCallback( ( href: Url ) => {
		if ( typeof href === 'string' ) {
			return href;
		}

		const nextPathname = href.pathname || '';
		const hash = href.hash || '';
		const query = href.query
			? new URLSearchParams(
				Object.entries( href.query ).flatMap( ( [ key, value ] ) => {
					if ( value === undefined ) {
						return [];
					}

					if ( Array.isArray( value ) ) {
						return value.map( ( item ) => [ key, `${ item }` ] );
					}

					return [ [ key, `${ value }` ] ];
				} )
			  ).toString()
			: '';

		return `${ nextPathname }${ query ? `?${ query }` : '' }${ hash }`;
	}, [] );

	const emit = useCallback(
		( eventName: BlockingRouterEventName, url: string ) => {
			const handlers = eventHandlersRef.current.get( eventName );
			if ( !handlers ) {
				return;
			}

			for ( const handler of handlers ) {
				handler( url );
			}
		},
		[]
	);

	useEffect( () => {
		currentUrlRef.current = currentUrl;

		if ( pendingPushRef.current?.targetUrl === currentUrl ) {
			emit( 'routeChangeComplete', currentUrl );
			pendingPushRef.current.resolve( true );
			pendingPushRef.current = null;
		}
	}, [ currentUrl, emit ] );

	const events = useMemo<BlockingRouterEvents>(
		() => ( {
			on: ( eventName, handler ) => {
				const handlers =
					eventHandlersRef.current.get( eventName ) ||
					new Set<BlockingRouterEventHandler>();

				handlers.add( handler );
				eventHandlersRef.current.set( eventName, handlers );
			},
			off: ( eventName, handler ) => {
				const handlers = eventHandlersRef.current.get( eventName );
				if ( !handlers ) {
					return;
				}

				handlers.delete( handler );
				if ( handlers.size === 0 ) {
					eventHandlersRef.current.delete( eventName );
				}
			},
		} ),
		[]
	);

	const pushAndWaitForCompletion = useCallback(
		(
			href: Url,
			nextRouterOptions?: Parameters<ReturnType<typeof useRouter>['push']>[1]
		) => {
			const targetUrl = getNavigationTarget( href );

			if ( targetUrl === currentUrlRef.current ) {
				router.push( targetUrl, nextRouterOptions );
				return Promise.resolve( true );
			}

			return new Promise<boolean>( ( resolve ) => {
				pendingPushRef.current = {
					targetUrl,
					resolve,
				};

				emit( 'beforeHistoryChange', targetUrl );
				emit( 'routeChangeStart', targetUrl );

				try {
					router.push( targetUrl, nextRouterOptions );
				} catch ( error ) {
					emit( 'routeChangeError', targetUrl );
					pendingPushRef.current = null;
					resolve( false );
					throw error;
				}
			} );
		},
		[
			emit,
			getNavigationTarget,
			router
		]
	);

	const push = useCallback(
		async ( href: Url, options?: UseBlockingRouterOptions ): Promise<boolean> => {
			const { overrideBlockers, bypassBlockers, ...nextRouterOptions } =
				options || {};

			if ( ctx && overrideBlockers ) {
				for ( const id of overrideBlockers ) {
					ctx.removeBlocker( id );
				}
			}

			if ( !ctx || !ctx.hasBlockers() ) {
				return pushAndWaitForCompletion( href, nextRouterOptions );
			}

			const allowed = await ctx.executeBlockers( href, bypassBlockers );
			if ( allowed ) {
				return pushAndWaitForCompletion( href, nextRouterOptions );
			}

			emit( 'routeChangeError', getNavigationTarget( href ) );

			return false;
		},
		[
			ctx,
			emit,
			getNavigationTarget,
			pushAndWaitForCompletion
		]
	);

	const query = useMemo( () => {
		const nextQuery: Record<string, string | string[]> = {};

		if ( searchParams ) {
			const keys = new Set( searchParams.keys() );

			for ( const key of keys ) {
				const values = searchParams.getAll( key );
				nextQuery[ key ] = values.length > 1 ? values : values[ 0 ] ?? '';
			}
		}

		return nextQuery;
	}, [ searchParams ] );

	return useMemo(
		() => ( { ...router, asPath, events, query, pathname, push } ),
		[
			asPath,
			events,
			router,
			query,
			pathname,
			push
		]
	);
};
